import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Input } from "reactstrap";

/**
 * Kendi kendine yeten, client-side zengin tablo:
 * - global arama + kolon başına filtre
 * - kolon başlığına tıklayarak sıralama (asc/desc/kapalı)
 * - sayfalama + sayfa boyutu
 * - kolonları sürükle-bırak ile taşıma + göster/gizle (localStorage'da kalıcı)
 * Ortak Datagrid'i etkilemez; tek sayfaya özel kullanılır.
 */
export interface RichColumn<T = any> {
  key: string;
  label: string;
  render?: (row: T) => React.ReactNode; // görüntü (varsayılan: ham değer)
  value?: (row: T) => any; // sıralama/arama için ham değer (varsayılan: row[key])
  sortable?: boolean; // default true
  filterable?: boolean; // default true
  width?: string;
}

interface RichTableProps<T = any> {
  tableId?: string;
  columns: RichColumn<T>[];
  rows: T[];
  rowKey?: (row: T) => string;
  loading?: boolean;
  pageSizeOptions?: number[];
  actions?: (row: T) => React.ReactNode;
  actionsLabel?: string;
  emptyText?: string;
  toolbarRight?: React.ReactNode;
  t?: (k: string, d?: string) => string;
}

const txt = (v: any) => (v == null ? "" : String(v));
function cellVal<T>(c: RichColumn<T>, row: T): any {
  return c.value ? c.value(row) : (row as any)[c.key];
}

function RichTable<T = any>({
  tableId = "richtable",
  columns,
  rows,
  rowKey,
  loading,
  pageSizeOptions = [10, 25, 50, 100],
  actions,
  actionsLabel = "",
  emptyText = "Kayıt yok",
  toolbarRight,
  t,
}: RichTableProps<T>) {
  const tr = (k: string, d: string) => (t ? t(k, d) : d);
  const LS_ORDER = "rt-order-" + tableId;
  const LS_HIDE = "rt-hide-" + tableId;
  const allKeys = useMemo(() => columns.map((c) => c.key), [columns]);

  const [order, setOrder] = useState<string[]>(() => {
    try {
      const s = JSON.parse(localStorage.getItem(LS_ORDER) || "null");
      if (Array.isArray(s)) {
        const valid = s.filter((k: string) => allKeys.includes(k));
        const missing = allKeys.filter((k) => !valid.includes(k));
        return [...valid, ...missing];
      }
    } catch (e) {}
    return allKeys;
  });
  const [hidden, setHidden] = useState<string[]>(() => {
    try {
      const s = JSON.parse(localStorage.getItem(LS_HIDE) || "[]");
      return Array.isArray(s) ? s : [];
    } catch (e) {
      return [];
    }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_ORDER, JSON.stringify(order)); } catch (e) {}
  }, [order, LS_ORDER]);
  useEffect(() => {
    try { localStorage.setItem(LS_HIDE, JSON.stringify(hidden)); } catch (e) {}
  }, [hidden, LS_HIDE]);

  const colMap = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.key, c])) as Record<string, RichColumn<T>>,
    [columns]
  );
  const orderedCols = useMemo(
    () => order.map((k) => colMap[k]).filter(Boolean) as RichColumn<T>[],
    [order, colMap]
  );
  const visibleCols = useMemo(
    () => orderedCols.filter((c) => !hidden.includes(c.key)),
    [orderedCols, hidden]
  );

  const [search, setSearch] = useState("");
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showCols, setShowCols] = useState(false);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0] || 10);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const activeFilters = Object.entries(colFilters).filter(([, v]) => (v || "").trim());
    return rows.filter((row) => {
      if (q && !visibleCols.some((c) => txt(cellVal(c, row)).toLowerCase().includes(q))) return false;
      for (const [k, v] of activeFilters) {
        const c = colMap[k];
        if (c && !txt(cellVal(c, row)).toLowerCase().includes(v.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [rows, search, colFilters, visibleCols, colMap]);

  const sorted = useMemo(() => {
    if (!sortKey || !colMap[sortKey]) return filtered;
    const c = colMap[sortKey];
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = cellVal(c, a);
      const bv = cellVal(c, b);
      let cmp: number;
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else cmp = txt(av).localeCompare(txt(bv), undefined, { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortDir, colMap]);

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const curPage = Math.min(page, pageCount);
  const paged = useMemo(
    () => sorted.slice((curPage - 1) * pageSize, curPage * pageSize),
    [sorted, curPage, pageSize]
  );
  useEffect(() => { setPage(1); }, [search, colFilters, pageSize]);

  const toggleSort = (c: RichColumn<T>) => {
    if (c.sortable === false) return;
    if (sortKey !== c.key) { setSortKey(c.key); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortKey(null); setSortDir("asc"); }
  };

  const dragKey = useRef<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const onDrop = (targetKey: string) => {
    const from = dragKey.current;
    dragKey.current = null;
    setDragOver(null);
    if (!from || from === targetKey) return;
    setOrder((prev) => {
      const arr = prev.filter((k) => allKeys.includes(k));
      const fi = arr.indexOf(from);
      const ti = arr.indexOf(targetKey);
      if (fi < 0 || ti < 0) return prev;
      arr.splice(fi, 1);
      arr.splice(ti, 0, from);
      return arr;
    });
  };

  const pageNumbers = useMemo(() => {
    const nums: (number | "...")[] = [];
    const add = (n: number | "...") => nums.push(n);
    const win = 1;
    for (let p = 1; p <= pageCount; p++) {
      if (p === 1 || p === pageCount || (p >= curPage - win && p <= curPage + win)) add(p);
      else if (nums[nums.length - 1] !== "...") add("...");
    }
    return nums;
  }, [pageCount, curPage]);

  const rangeFrom = total === 0 ? 0 : (curPage - 1) * pageSize + 1;
  const rangeTo = Math.min(total, curPage * pageSize);

  return (
    <Card>
      <CardBody>
        {/* Araç çubuğu */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div className="search-box position-relative" style={{ minWidth: 240 }}>
            <Input
              type="text"
              className="form-control"
              placeholder={tr("Components.Search", "Ara…")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 32 }}
            />
            <i className="bx bx-search-alt" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#74788d" }} />
          </div>
          <div className="d-flex align-items-center gap-2 position-relative">
            <button
              type="button"
              className={"btn btn-sm " + (showFilters ? "btn-primary" : "btn-outline-secondary")}
              onClick={() => setShowFilters((s) => !s)}
              title={tr("Components.Filters", "Filtreler")}
            >
              <i className="mdi mdi-filter-variant me-1" />
              {tr("Components.Filters", "Filtreler")}
            </button>
            <div className="position-relative">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowCols((s) => !s)}
                title={tr("Components.Columns", "Kolonlar")}
              >
                <i className="mdi mdi-view-column me-1" />
                {tr("Components.Columns", "Kolonlar")}
              </button>
              {showCols && (
                <div
                  className="card shadow"
                  style={{ position: "absolute", right: 0, top: "110%", zIndex: 1000, minWidth: 220, padding: 10, maxHeight: 320, overflow: "auto" }}
                >
                  <div className="text-muted small mb-2">{tr("Components.ColumnsHint", "Sürükleyerek sırala, kutucukla göster/gizle")}</div>
                  {orderedCols.map((c) => (
                    <label key={c.key} className="d-flex align-items-center gap-2 py-1" style={{ cursor: "pointer", fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={!hidden.includes(c.key)}
                        onChange={() =>
                          setHidden((h) => (h.includes(c.key) ? h.filter((x) => x !== c.key) : [...h, c.key]))
                        }
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <select
              className="form-select form-select-sm"
              style={{ width: "auto" }}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n} / {tr("Components.Page", "sayfa")}</option>
              ))}
            </select>
            {toolbarRight}
          </div>
        </div>

        {/* Tablo */}
        <div className="table-responsive" style={{ position: "relative" }}>
          {loading && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,.6)", zIndex: 5, display: "grid", placeItems: "center" }}>
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}
          <table className="table align-middle table-nowrap table-hover table-header-colored mb-0">
            <thead>
              <tr>
                {visibleCols.map((c) => {
                  const active = sortKey === c.key;
                  return (
                    <th
                      key={c.key}
                      draggable
                      onDragStart={() => (dragKey.current = c.key)}
                      onDragOver={(e) => { e.preventDefault(); if (dragOver !== c.key) setDragOver(c.key); }}
                      onDragLeave={() => setDragOver((d) => (d === c.key ? null : d))}
                      onDrop={() => onDrop(c.key)}
                      onClick={() => toggleSort(c)}
                      style={{
                        cursor: c.sortable === false ? "move" : "pointer",
                        userSelect: "none",
                        width: c.width,
                        background: dragOver === c.key ? "rgba(85,110,230,.12)" : undefined,
                        whiteSpace: "nowrap",
                      }}
                      title={tr("Components.SortDragHint", "Tıkla: sırala · Sürükle: taşı")}
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        <i className="mdi mdi-drag-vertical text-muted" style={{ opacity: 0.5 }} />
                        {c.label}
                        {active && (
                          <i className={"mdi " + (sortDir === "asc" ? "mdi-arrow-up" : "mdi-arrow-down")} />
                        )}
                      </span>
                    </th>
                  );
                })}
                {actions && <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>{actionsLabel}</th>}
              </tr>
              {showFilters && (
                <tr>
                  {visibleCols.map((c) => (
                    <th key={c.key} style={{ padding: 6 }}>
                      {c.filterable === false ? null : (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder={tr("Components.Filter", "Filtre")}
                          value={colFilters[c.key] || ""}
                          onChange={(e) => setColFilters((f) => ({ ...f, [c.key]: e.target.value }))}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </th>
                  ))}
                  {actions && <th />}
                </tr>
              )}
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={visibleCols.length + (actions ? 1 : 0)} className="text-center text-muted py-4">
                    {emptyText}
                  </td>
                </tr>
              ) : (
                paged.map((row, i) => (
                  <tr key={rowKey ? rowKey(row) : i}>
                    {visibleCols.map((c) => (
                      <td key={c.key}>{c.render ? c.render(row) : txt(cellVal(c, row))}</td>
                    ))}
                    {actions && (
                      <td>
                        <div className="d-flex justify-content-end gap-1">{actions(row)}</div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Alt bilgi + sayfalama */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 gap-2">
          <div className="text-muted small">
            {total === 0
              ? tr("Components.NoRecords", "Kayıt yok")
              : `${rangeFrom}-${rangeTo} / ${total}`}
          </div>
          {pageCount > 1 && (
            <ul className="pagination pagination-rounded mb-0">
              <li className={"page-item " + (curPage <= 1 ? "disabled" : "")}>
                <button className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>{"<"}</button>
              </li>
              {pageNumbers.map((n, idx) =>
                n === "..." ? (
                  <li key={"e" + idx} className="page-item disabled"><span className="page-link">…</span></li>
                ) : (
                  <li key={n} className={"page-item " + (n === curPage ? "active" : "")}>
                    <button className="page-link" onClick={() => setPage(n as number)}>{n}</button>
                  </li>
                )
              )}
              <li className={"page-item " + (curPage >= pageCount ? "disabled" : "")}>
                <button className="page-link" onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>{">"}</button>
              </li>
            </ul>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export default RichTable;
